import React from 'react'
import { StairsPropertiesProps } from './types'
import {
  PropertySection,
  SectionHeader,
  SectionTitle,
  CollapseIcon,
  SectionContent,
  PropertyGroup,
  Label,
  Input,
  InfoText,
  Select,
  Checkbox,
  FlexRow,
  RangeInput,
  RangeValue,
} from '../styled'
import {
  getUnitSingular,
  getDefaultStepHeight,
  getDefaultTreadDepth,
  formatStandardStepHeight,
  formatStandardTreadDepth,
} from '../helpers'

const StairsProperties: React.FC<StairsPropertiesProps> = ({
  selectedElement,
  mapSettings,
  localProperties,
  isCollapsed,
  onToggleSection,
  updateProperty,
  onUpdateElement,
}) => {
  return (
    <PropertySection>
      <SectionHeader isCollapsed={isCollapsed} onClick={onToggleSection}>
        <SectionTitle>Stairs Properties</SectionTitle>
        <CollapseIcon isCollapsed={isCollapsed}>▼</CollapseIcon>
      </SectionHeader>
      <SectionContent isCollapsed={isCollapsed}>
        <PropertyGroup>
          <Label>Number of Steps</Label>
          <Input
            type="number"
            min="2"
            max="30"
            value={localProperties.steps || 12}
            onChange={e => updateProperty('steps', Number(e.target.value))}
          />
        </PropertyGroup>
        <PropertyGroup>
          <Label>Step Height ({getUnitSingular(mapSettings)})</Label>
          <Input
            type="number"
            step="0.01"
            value={(
              localProperties.stepHeight || getDefaultStepHeight(mapSettings)
            ).toFixed(2)}
            onChange={e => updateProperty('stepHeight', Number(e.target.value))}
          />
          <InfoText>Standard: {formatStandardStepHeight(mapSettings)}</InfoText>
        </PropertyGroup>
        <PropertyGroup>
          <Label>Tread Depth ({getUnitSingular(mapSettings)})</Label>
          <Input
            type="number"
            step="0.01"
            value={(
              localProperties.treadDepth || getDefaultTreadDepth(mapSettings)
            ).toFixed(2)}
            onChange={e => updateProperty('treadDepth', Number(e.target.value))}
          />
          <InfoText>Standard: {formatStandardTreadDepth(mapSettings)}</InfoText>
        </PropertyGroup>
        <PropertyGroup>
          <Label>Direction</Label>
          <Select
            value={localProperties.direction || 'up'}
            onChange={e => updateProperty('direction', e.target.value)}>
            <option value="up">Going Up</option>
            <option value="down">Going Down</option>
          </Select>
        </PropertyGroup>
        <PropertyGroup>
          <Label>Stair Lines Orientation</Label>
          <Select
            value={localProperties.stairOrientation || 'horizontal'}
            onChange={e => updateProperty('stairOrientation', e.target.value)}>
            <option value="horizontal">Horizontal</option>
            <option value="vertical">Vertical</option>
          </Select>
        </PropertyGroup>
        <PropertyGroup>
          <Label>
            <Checkbox
              checked={localProperties.hasHandrail || false}
              onChange={e => updateProperty('hasHandrail', e.target.checked)}
            />
            Has Handrail
          </Label>
        </PropertyGroup>
        <PropertyGroup>
          <Label>Material</Label>
          <Select
            value={localProperties.material || 'wood'}
            onChange={e => updateProperty('material', e.target.value)}>
            <option value="wood">Wood</option>
            <option value="concrete">Concrete</option>
            <option value="metal">Metal</option>
            <option value="carpet">Carpeted</option>
          </Select>
        </PropertyGroup>
        <PropertyGroup>
          <Label>Stairs Label</Label>
          <Input
            value={localProperties.label || ''}
            onChange={e => updateProperty('label', e.target.value)}
            placeholder="e.g., Main Stairs, Fire Escape"
          />
        </PropertyGroup>
        <PropertyGroup>
          <Label>Label Rotation (degrees)</Label>
          <FlexRow>
            <RangeInput
              min="-180"
              max="180"
              value={localProperties.labelRotation ?? 0}
              onChange={e =>
                updateProperty('labelRotation', Number(e.target.value))
              }
            />
            <Input
              type="number"
              min="-180"
              max="180"
              value={localProperties.labelRotation ?? 0}
              onChange={e =>
                updateProperty('labelRotation', Number(e.target.value))
              }
              style={{ width: '80px', marginLeft: '8px' }}
            />
            <RangeValue>{localProperties.labelRotation ?? 0}°</RangeValue>
          </FlexRow>
        </PropertyGroup>
        <PropertyGroup>
          <Label>
            <Checkbox
              checked={selectedElement.locked || false}
              onChange={e =>
                onUpdateElement(selectedElement.id, {
                  locked: e.target.checked,
                })
              }
            />
            Lock from Dragging
          </Label>
        </PropertyGroup>
      </SectionContent>
    </PropertySection>
  )
}

export { StairsProperties }
